import React, { useState, useEffect } from "react";
import axios from "axios";
import { Table, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import PromotionModal from "./PromotionModal";

const Promotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [show, setShow] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(false);
  const [currentPromotion, setCurrentPromotion] = useState(null);
  const [promotionData, setPromotionData] = useState({
    Title: "",
    Description: "",
    StartDate: "",
    EndDate: "",
    Redirect_URL: "",
    ImagePath: "", // This will hold a file reference for uploading
  });

  // Modal Handlers
  const handleShow = () => setShow(true);
  const handleClose = () => {
    setShow(false);
    setEditingPromotion(false);
    setPromotionData({
      Title: "",
      Description: "",
      StartDate: "",
      EndDate: "",
      Redirect_URL: "",
      ImagePath: "",
    });
  };
  const API_URL = process.env.REACT_APP_API_URL; //${API_URL}
  const IMAGE_URL =
    process.env.REACT_APP_IMAGE_URL ||
    "https://selfcareimages.americanholdings.lk"; //${API_URL}
  // Fetch promotions on component mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      const token = localStorage.getItem("authToken"); // Retrieve authToken from localStorage
      const response = await axios.get(`${API_URL}/api/promotions`, {
        headers: {
          Authorization: `Bearer ${token}`, // Include the token in the headers
        },
      });
      setPromotions(response.data);
    } catch (error) {
      console.error("Error fetching promotions:", error);
    }
  };

  // Format date to a readable string (e.g., "MM/DD/YYYY")
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Handle input change in the form
  const handleChange = (e) => {
    const { name, value } = e.target;
    setPromotionData({ ...promotionData, [name]: value });
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setPromotionData({ ...promotionData, [name]: files[0] });
  };

  const handleSave = async () => {
    try {
      const {
        Title,
        Description,
        StartDate,
        EndDate,
        Redirect_URL,
        ImagePath,
      } = promotionData;

      // Simple field presence validation
      if (
        !Title ||
        !Description ||
        !StartDate ||
        !EndDate ||
        !Redirect_URL ||
        !ImagePath
      ) {
        Swal.fire({
          icon: "warning",
          title: "Missing Fields",
          text: "Please fill in all required fields.",
        });
        return;
      }

      // Validate dates
      const start = new Date(StartDate);
      const end = new Date(EndDate);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Dates",
          text: "Please select valid Start and End dates.",
        });
        return;
      }

      if (end < start) {
        Swal.fire({
          icon: "warning",
          title: "Date Range Error",
          text: "End Date must be later than or equal to Start Date.",
        });
        return;
      }

      // Validate file size (< 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (ImagePath.size > maxSize) {
        Swal.fire({
          icon: "error",
          title: "File Too Large",
          text: "Image size must be less than 5 MB.",
        });
        return;
      }

      // Validate 2:1 aspect ratio
      const img = new Image();
      img.src = URL.createObjectURL(ImagePath);
      img.onload = async () => {
        const ratio = img.width / img.height;
        if (Math.abs(ratio - 2) > 0.01) {
          // Allow a tiny margin of error
          Swal.fire({
            icon: "error",
            title: "Invalid Image Ratio",
            text: `Image must have a 2:1 ratio. Your image is ${img.width}x${img.height}px.`,
          });
          return;
        }

        // All validations passed
        const formData = new FormData();
        formData.append("Title", Title);
        formData.append("Description", Description);
        formData.append("StartDate", start.toISOString().split("T")[0]);
        formData.append("EndDate", end.toISOString().split("T")[0]);
        formData.append("Redirect_URL", Redirect_URL);
        formData.append("ImagePath", ImagePath);

        const token = localStorage.getItem("authToken");
        let response;

        if (editingPromotion) {
          response = await axios.put(
            `${API_URL}/api/promotions/${currentPromotion.id}`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${token}`,
              },
            },
          );
          Swal.fire({
            icon: "success",
            title: "Promotion Updated!",
            showConfirmButton: false,
            timer: 1500,
          });
        } else {
          response = await axios.post(`${API_URL}/api/promotions`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          });
          Swal.fire({
            icon: "success",
            title: "Promotion Added!",
            showConfirmButton: false,
            timer: 1500,
          });
        }

        console.log("Promotion saved successfully", response.data);
        handleClose();
        fetchPromotions();
      };
    } catch (error) {
      console.error(
        "Error saving promotion:",
        error.response ? error.response.data : error,
      );
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "There was an error saving the promotion.",
      });
    }
  };

  // Delete promotion
  const handleDeletePromotion = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("authToken"); // Retrieve authToken from localStorage
          await axios.delete(`${API_URL}/api/promotions/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`, // Include the token in the headers
            },
          });
          Swal.fire({
            icon: "success",
            title: "Promotion Deleted!",
            showConfirmButton: false,
            timer: 1500,
          });
          fetchPromotions();
        } catch (error) {
          console.error("Error deleting promotion:", error);
        }
      }
    });
  };

  return (
    <div className="page ">
      <h1>Promotions</h1>
      <p>Manage your Promotions here.</p>
      <Button variant="primary" onClick={handleShow} className="mb-3">
        Add Promotion
      </Button>

      {/* Promotions Table */}
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>#</th>
            <th>Title</th>
            <th>Description</th>
            <th>URL</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Image</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {promotions.map((promotion, index) => (
            <tr key={promotion.Id}>
              <td>{index + 1}</td>
              <td>{promotion.Title || "N/A"}</td>
              <td>{promotion.Description || "N/A"}</td>
              <td>
                {promotion.Redirect_URL ? (
                  <a
                    href={promotion.Redirect_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Link
                  </a>
                ) : (
                  "N/A"
                )}
              </td>

              <td>{formatDate(promotion.StartDate) || "N/A"}</td>
              <td>{formatDate(promotion.EndDate) || "N/A"}</td>
              <td>
                <img
                  loading="lazy"
                  src={`${IMAGE_URL}/${promotion.ImagePath}`}
                  alt="Promotion"
                  style={{
                    width: "200px",
                    height: "auto",
                    marginBottom: "10px",
                  }}
                />
              </td>

              <td>
                {/* <Button
                  variant="warning"
                  className="me-2"
                  onClick={() => handleEditPromotion(promotion.id)}
                >
                  Edit
                </Button> */}
                <Button
                  variant="danger"
                  onClick={() => handleDeletePromotion(promotion.Id)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Promotion Modal */}
      <PromotionModal
        show={show}
        handleClose={handleClose}
        promotionData={promotionData}
        handleChange={handleChange}
        handleFileChange={handleFileChange}
        handleSave={handleSave}
        editingPromotion={editingPromotion}
      />
    </div>
  );
};

export default Promotions;
